import { getOrganizationRoles } from "../../../../../utils/organization-roles";

const ORGANIZATION_ROLE_I18N_KEYS = {
  Admin: "manageCoaches.roleAdmin",
  Coach: "manageCoaches.roleCoach",
  Nutritionist: "manageCoaches.roleNutritionist",
  Owner: "manageCoaches.roleOwner",
  "Team Coach": "manageCoaches.roleTeamCoach",
};

const ORGANIZATION_PERMISSION_I18N_KEYS = {
  "Custom Workflows": "manageCoaches.permissionCustomWorkflows",
  "Exercise Library": "manageCoaches.permissionExerciseLibrary",
  PERFORM: "manageCoaches.permissionPerform",
  PRESS: "manageCoaches.pressGymManagement",
  PUSH: "manageCoaches.pushSubscriptions",
  Templates: "manageCoaches.permissionTemplates",
  Widgets: "manageCoaches.permissionWidgets",
};

/**
 * @param {function(string): string} t i18next `t` (dashboard namespace)
 * @param {string} englishRoleName
 * @returns {string}
 */
export const translateOrganizationRole = (t, englishRoleName) =>
  englishRoleName && ORGANIZATION_ROLE_I18N_KEYS[englishRoleName]
    ? t(ORGANIZATION_ROLE_I18N_KEYS[englishRoleName])
    : englishRoleName || "";

/**
 * @param {function(string): string} t i18next `t` (dashboard namespace)
 * @param {string} englishPermissionName
 * @returns {string}
 */
export const translateOrganizationPermission = (t, englishPermissionName) =>
  englishPermissionName && ORGANIZATION_PERMISSION_I18N_KEYS[englishPermissionName]
    ? t(ORGANIZATION_PERMISSION_I18N_KEYS[englishPermissionName])
    : englishPermissionName || "";

/**
 * Get the role name if we send the role id
 * @param {*} organizationRoles
 * @param {*} organizationRoleId
 * @returns
 */
export const getRoleNameById = (organizationRoles = [], organizationRoleId) => {
  const foundRole = organizationRoles.find(
    ({ _id }) => _id === organizationRoleId
  );
  return foundRole ? foundRole.role : "";
};

/**
 * Organization members who can be picked as a client's coach.
 *
 * A Team Coach works across the whole client base and never holds assigned clients, so
 * offering one here would produce an assignment the API rejects anyway (the same rule is
 * enforced server-side in organization-re-assign-coach.js and pull-invite.js). Resolves
 * the roles itself because the role list is a static local module, not loaded state.
 *
 * @param {Array} organizationActiveUsers
 * @returns {Array}
 */
export const getUsersAssignableAsCoach = (organizationActiveUsers = []) => {
  const organizationRoles = getOrganizationRoles();
  return organizationActiveUsers.filter(
    ({ organizationRoleId }) =>
      getRoleNameById(organizationRoles, organizationRoleId) !== "Team Coach"
  );
};

/**
 * The coach pre-selected in an "assign a coach" picker: the viewer, except a Team Coach,
 * who can never hold clients. For them it is `preferredCoachId` when that is an
 * assignable member (acknowledging a client shouldn't silently move them), otherwise the
 * Owner, matching the API's fallback in pull-invite.js. Falls back to the viewer if no
 * Owner is in the list.
 *
 * @param {Object} activeUser
 * @param {Array} organizationActiveUsers
 * @param {string} [preferredCoachId]
 * @returns {Object}
 */
export const getDefaultAssignedCoach = (
  activeUser,
  organizationActiveUsers = [],
  preferredCoachId
) => {
  const organizationRoles = getOrganizationRoles();
  const roleNameOf = ({ organizationRoleId }) =>
    getRoleNameById(organizationRoles, organizationRoleId);
  if (roleNameOf(activeUser) !== "Team Coach") return activeUser;
  const preferred = getUsersAssignableAsCoach(organizationActiveUsers).find(
    ({ _id }) => preferredCoachId && _id === preferredCoachId
  );
  return (
    preferred ||
    organizationActiveUsers.find((user) => roleNameOf(user) === "Owner") ||
    activeUser
  );
};

/**
 * Get all the permission's names if we send the ids
 * @param {*} organizationPermissions
 * @param {*} organizationUserPermissions
 * @param {function(string): string} [t] i18next `t` (dashboard namespace)
 * @returns
 */
export const getPermissionNamesByIds = (
  organizationPermissions = [],
  organizationUserPermissions,
  t
) => {
  const permissionNames = organizationUserPermissions.map((permissionId) => {
    const foundPermission = organizationPermissions.find(
      ({ _id }) => _id === permissionId
    );
    if (!foundPermission) {
      return "";
    }
    const raw = foundPermission.permission;
    return t ? translateOrganizationPermission(t, raw) : raw;
  });

  return permissionNames.join(", ");
};

/**
 * Get the id of the role
 * @param {*} roles
 * @param {*} roleTofind
 * @returns
 */
export const getRoleId = (roles, roleTofind) =>
  roles.find(({ role }) => role === roleTofind)._id;

/**
 * Check if the user is a admin or owner of the organization
 * @param {*} organizationRoleId
 * @param {*} ownerRoleId
 * @param {*} adminRoleId
 * @returns
 */
export const getIsAdminOrOwner = (
  organizationRoleId = "",
  ownerRoleId = "",
  adminRoleId = ""
) =>
  organizationRoleId.toString() === ownerRoleId.toString() ||
  organizationRoleId.toString() === adminRoleId.toString();

/**
 * Filter the roles to show in the dropdown:
 * - Owner can invite anyone but another Owner (only one owner).
 * - Admin can invite anyone but Admin or Owner — only the Owner creates Admins.
 * - Team Coach can invite anyone but Admin or Owner, like an Admin.
 * - Everyone else (Coach, Nutritionist, ...) can invite Coach or Nutritionist
 *   only — Team Coach has org-wide scope, so only Owner/Admin may grant it.
 * @param {*} organizationRoles
 * @param {*} roleName
 * @returns
 */
export const getRolesToShow = (organizationRoles = [], roleName) => {
  if (roleName === "Owner") {
    return organizationRoles.filter(({ role }) => role !== "Owner");
  }
  if (roleName === "Admin" || roleName === "Team Coach") {
    return organizationRoles.filter(
      ({ role }) => role !== "Admin" && role !== "Owner"
    );
  }
  return organizationRoles.filter(
    ({ role }) => role !== "Admin" && role !== "Owner" && role !== "Team Coach"
  );
};

/**
 * Check if we have to use the current user's information or the organization owner's information
 * If the user is an athlete, or the user is not member of an organization the value should be active user if not organization owner
 * @param {*} activeUser
 * @param {*} organizationOwner
 * @returns
 */
export const getUserToRequest = (activeUser, organizationOwner) => {
  const userIsCoach = activeUser?.permissions?.includes("coach");

  const userToRequest =
    (!activeUser?.organizationId && !organizationOwner) || !userIsCoach
      ? activeUser
      : organizationOwner;

  return userToRequest;
};
